import React from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import {
  useDeleteSurveyMutation,
  useGetMemberVisibleSurveysQuery,
  useGetSurveysQuery,
} from "../../../features/surveys/surveysApiSlice";
import { setDialog } from "../../../features/dialog/dialogSlice";
import { useDispatch } from "react-redux";
import SurveyItem from "./SurveyItem";
import Iconify from "../../../components/iconify";
import Loading from "../../../components/spinners/Loading";
import useAuth from "../../../hooks/useAuth";

function DisplaySurveys() {
  const { isMember } = useAuth();
  const adminSurveys = useGetSurveysQuery(undefined, { skip: isMember });
  const memberSurveys = useGetMemberVisibleSurveysQuery(undefined, {
    skip: !isMember,
  });

  const surveys = isMember ? memberSurveys.data : adminSurveys.data;
  const isFetching = isMember ? memberSurveys.isFetching : adminSurveys.isFetching;
  const isLoading = isMember ? memberSurveys.isLoading : adminSurveys.isLoading;

  const dispatch = useDispatch();
  const [deleteSurvey] = useDeleteSurveyMutation(); // For deleting surveys

  const openDialog = () => {
    dispatch(
      setDialog({
        title: "Befragung erstellen",
        content: "SURVEY",
        type: "CREATE",
        survey: {
          surveyTitle: "",
          surveyDescription: "",
          protection: true,
          surveyQuestions: [],
          targeting: {
            enabled: false,
            siteIds: [],
            minDegree: null,
            maxDegree: null,
            minAge: null,
            maxAge: null,
            memberIds: [],
          },
        },
      }),
    );
  };

  const openEditSurveyDialog = (survey) => {
    dispatch(
      setDialog({
        title: "Befragung bearbeiten",
        content: "SURVEY",
        type: "EDIT",
        survey,
      }),
    );
  };

  const openShowSurveyQRDialog = (survey) => {
    let parsedURL = new URL(window.location.href);

    dispatch(
      setDialog({
        title: "QR Code für Befragung",
        content: "QR",
        type: "SHOW",
        value: `${parsedURL.origin}/survey/${survey._id}`,
      }),
    );
  };

  const handleDelete = async (surveyId, surveyTitle) => {
    try {
      let response = await deleteSurvey({ _id: surveyId }).unwrap(); // Call the delete API
      if (response && response?.status === "success") {
        dispatch(
          setDialog({
            title: "Befragung gelöscht!",
            type: "SUCCESS",
            data: {
              successText: `Befragung ${surveyTitle} wurde erfolgreich gelöscht!`,
            },
          }),
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
    }
  };

  if (isLoading || isFetching) {
    return <Loading />;
  }

  return (
    <div>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 3 },
          mt: 1.25,
          borderRadius: 2,
          background:
            "linear-gradient(125deg, rgba(253, 251, 246, 1) 0%, rgba(240, 248, 255, 1) 100%)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography textAlign="center" variant="h3" marginBottom={1.5}>
          {isMember ? "Befragungen für mich" : "Standort- und Mitgliederbefragung"}
        </Typography>

        <Box sx={{ mb: 1.25 }}>
          <Typography variant="h6">
            {isMember
              ? "Hier sehen Sie Befragungen, die für Ihr Mitgliedsprofil freigeschaltet wurden."
              : "Hier können Sie Fragebögen für Standorte und Mitglieder erstellen. Diese Fragebögen können dann ausgedruckt werden oder via Link oder QR-Code verteilt werden."}
          </Typography>
        </Box>

        {!isMember && (
          <Button
            onClick={openDialog}
            variant="contained"
            sx={{ minWidth: 300 }}
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            Neue Befragung
          </Button>
        )}
      </Paper>
      <div style={{ marginTop: 15 }}>
        <Grid container spacing={2}>
          {surveys && surveys.length > 0 ? (
            surveys.map((survey) => (
              <Grid item xs={12} md={4} key={survey._id}>
                <SurveyItem
                  survey={survey}
                  handleDelete={handleDelete}
                  handleEdit={openEditSurveyDialog}
                  handleQR={openShowSurveyQRDialog}
                  mode={isMember ? "member" : "admin"}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="h6" color="textSecondary" align="center">
                {isMember
                  ? "Aktuell sind keine Befragungen für Sie freigeschaltet."
                  : "Es wurden noch keine Befragungen erstellt"}
              </Typography>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
}

export default DisplaySurveys;
